import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { APP_NAME } from "@/constants/app";
import { api } from "@/lib/api";

export default function Footer() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim() || !form.mobile.trim()) {
      alert("All fields are mandatory.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiry", form);
      setSuccess(true);
      setForm({ fullName: "", email: "", mobile: "" });
    } catch (err) {
      console.error(err);
      alert("Failed to submit enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <footer
      data-testid="app-footer"
      className="bg-gradient-to-br from-slate-50 via-white to-blue-50/50 border-t-4 border-sif-primary pt-12 pb-8 px-4 sm:px-6 lg:px-8 mt-12 relative z-10 shadow-inner"
    >
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {/* Column 1: Brand & Contact Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="w-10 h-10 rounded-xl bg-sif-primary flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 leading-tight">
                  {APP_NAME}
                </div>
                <div className="text-[10px] font-semibold text-sif-primary tracking-wider uppercase">
                  Specialized Investment Funds
                </div>
              </div>
            </Link>

            {/* ARN Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs font-semibold text-sif-primary">
              <ShieldCheck className="w-4 h-4 text-sif-primary" />
              <span>ASHISH RAMESH BHAVE - ARN71122 Pune<span className="text-amber-600 font-bold"></span></span>
            </div>

            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed max-w-sm font-medium">
              India's Premier Platform for Specialised Investment Funds (SIFs). Real-time analytics, fund screeners, and portfolio insights.
            </p>

            {/* Contact Details */}
            <div className="space-y-2.5 pt-1">
              <a
                href="mailto:arbfinancials@gmail.com"
                className="flex items-center gap-3 text-xs sm:text-sm text-slate-600 hover:text-sif-primary transition-colors group"
                data-testid="footer-email"
              >
                <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-sif-primary group-hover:bg-sif-primary group-hover:text-white transition-colors shrink-0 shadow-xs">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <span className="font-medium">arbfinancials@gmail.com</span>
              </a>

              <a
                href="tel:+919307487371"
                className="flex items-center gap-3 text-xs sm:text-sm text-slate-600 hover:text-sif-primary transition-colors group"
                data-testid="footer-phone"
              >
                <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-sif-primary group-hover:bg-sif-primary group-hover:text-white transition-colors shrink-0 shadow-xs">
                  <Phone className="w-3.5 h-3.5" />
                </div>
                <span className="font-medium">+91 9307487371</span>
              </a>

              <div
                className="flex items-center gap-3 text-xs sm:text-sm text-slate-600"
                data-testid="footer-location"
              >
                <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-sif-primary shrink-0 shadow-xs">
                  <MapPin className="w-3.5 h-3.5" />
                </div>
                <span className="font-medium">Pune, India</span>
              </div>
            </div>
          </div>

          {/* Column 2: Navigation Links */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 border-b-2 border-sif-primary/20 pb-2 mb-3 inline-block">
                Platform
              </h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>
                  <Link
                    to="/"
                    className="text-slate-600 hover:text-sif-primary transition-all flex items-center gap-1.5 hover:translate-x-1 group font-medium"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-sif-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    Home / Funds
                  </Link>
                </li>
                <li>
                  <Link
                    to="/compare"
                    className="text-slate-600 hover:text-sif-primary transition-all flex items-center gap-1.5 hover:translate-x-1 group font-medium"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-sif-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    Compare SIFs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/amcs"
                    className="text-slate-600 hover:text-sif-primary transition-all flex items-center gap-1.5 hover:translate-x-1 group font-medium"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-sif-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    AMC Scorecard
                  </Link>
                </li>
                <li>
                  <a
                    href="#analytics"
                    className="text-slate-600 hover:text-sif-primary transition-all flex items-center gap-1.5 hover:translate-x-1 group font-medium"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-sif-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    Risk Analytics
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 border-b-2 border-sif-primary/20 pb-2 mb-3 inline-block">
                Resources
              </h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>
                  <a
                    href="https://www.sebi.gov.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-sif-primary transition-all flex items-center gap-1.5 hover:translate-x-1 group font-medium"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-sif-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    SEBI Framework
                    <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-sif-primary ml-0.5" />
                  </a>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-slate-600 hover:text-sif-primary transition-all flex items-center gap-1.5 hover:translate-x-1 group font-medium"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-sif-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-slate-600 hover:text-sif-primary transition-all flex items-center gap-1.5 hover:translate-x-1 group font-medium"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-sif-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Column 3: Contact Form */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 border-b-2 border-sif-primary/20 pb-2 inline-block">
              Quick Enquiry
            </h4>
            {success ? (
              <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-200 text-center space-y-2">
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center mx-auto">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-xs font-bold text-emerald-800">Enquiry Submitted!</p>
                <p className="text-[11px] text-emerald-600">We will contact you shortly.</p>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  className="text-[10px] text-sif-primary font-semibold hover:underline"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-2.5">
                <input
                  type="text"
                  required
                  placeholder="Full Name *"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-sif-primary focus:border-sif-primary transition-all"
                />
                <input
                  type="email"
                  required
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-sif-primary focus:border-sif-primary transition-all"
                />
                <input
                  type="tel"
                  required
                  placeholder="Mobile Number *"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-sif-primary focus:border-sif-primary transition-all"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 rounded-lg bg-sif-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-all shadow-xs"
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-6 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div className="space-y-1.5 max-w-4xl">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">
              © {new Date().getFullYear()} {APP_NAME} All Rights Reserved. SIFs are regulated by SEBI.
            </p>
            <p className="text-xs text-slate-500 leading-normal">
              Investments in securities markets are subject to market risks. The content and information provided on this platform are for educational and informational purposes only and should not be considered financial or investment advice.
            </p>
          </div>

          <div className="flex items-center gap-4 text-xs font-semibold text-slate-600 shrink-0">
            <Link
              to="/privacy"
              data-testid="footer-link-privacy"
              className="hover:text-sif-primary transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-slate-300">•</span>
            <Link
              to="/terms"
              data-testid="footer-link-terms"
              className="hover:text-sif-primary transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
